import { faTriangleExclamation, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MaintenancePlanCode } from "@boark/shared/types/maintenance-plan-code";
import { useMemo, useState } from "react";

import { useLocale } from "../stores/locale";
import { recordIdToId } from "../utils/record-id-to-id";
import { Button } from "./button";
import { Input } from "./input";

export type MaintenancePlanGroupFormValues = {
  name: string;
};

export function MaintenancePlanGroupForm({
  onCancel,
  onChange,
  onSubmit,
  values,
  codes = [],
  onAddCode,
  onRemoveCode,
  loading = false,
  disabled = false,
  hasError = false,
  codeError = false,
  isEdit = false,
}: {
  onCancel: () => void;
  onChange: (values: MaintenancePlanGroupFormValues) => void;
  onSubmit: () => void;
  values: MaintenancePlanGroupFormValues;
  codes?: MaintenancePlanCode[];
  onAddCode?: (code: string) => Promise<void>;
  onRemoveCode?: (codeId: string) => Promise<void>;
  loading?: boolean;
  disabled?: boolean;
  hasError?: boolean;
  codeError?: boolean;
  isEdit?: boolean;
}) {
  const t = useLocale((state) => state.locale);
  const [newCode, setNewCode] = useState("");
  const [codeLoading, setCodeLoading] = useState(false);

  const isValid = useMemo(() => values.name.trim() !== "", [values.name]);
  const isDisabled = disabled || loading;

  return (
    <form
      className="flex-1 flex flex-col justify-between border-1 border-gray-300 rounded-xs p-4 min-w-md"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="flex flex-col gap-4">
        <Input
          required
          disabled={isDisabled}
          label={t.maintenance.groupName}
          type="text"
          value={values.name}
          onChange={(e) => onChange({ ...values, name: e.target.value })}
        />

        {isEdit && onAddCode && onRemoveCode && (
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-2">
              {t.maintenance.codes}
            </label>
            <ul className="border border-gray-300 rounded-xs divide-y mb-2">
              {codes.length === 0 && (
                <li className="p-2 text-gray-400 text-sm">
                  {t.maintenance.codesEmpty}
                </li>
              )}
              {codes.map((code) => (
                <li
                  key={recordIdToId(code.id)}
                  className="flex items-center justify-between p-2"
                >
                  <span>{code.code}</span>
                  <button
                    className="text-red-600 cursor-pointer disabled:opacity-50"
                    disabled={codeLoading || isDisabled}
                    type="button"
                    onClick={() => {
                      void (async () => {
                        setCodeLoading(true);
                        try {
                          await onRemoveCode(recordIdToId(code.id));
                        } finally {
                          setCodeLoading(false);
                        }
                      })();
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex gap-2">
              <Input
                className="flex-1"
                disabled={codeLoading || isDisabled}
                placeholder={t.maintenance.codePlaceholder}
                type="text"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
              />
              <Button
                disabled={codeLoading || isDisabled || newCode.trim() === ""}
                loading={codeLoading}
                type="button"
                onClick={() => {
                  void (async () => {
                    setCodeLoading(true);
                    try {
                      await onAddCode(newCode.trim());
                      setNewCode("");
                    } finally {
                      setCodeLoading(false);
                    }
                  })();
                }}
              >
                {t.maintenance.addCode}
              </Button>
            </div>
            {codeError && (
              <p className="text-red-600 text-sm mt-2">{t.maintenance.codeError}</p>
            )}
          </div>
        )}
      </div>

      {hasError && (
        <div className="flex items-center gap-2 text-red-600 mt-4">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>{t.maintenance.groupError}</span>
        </div>
      )}

      <div className="flex justify-end gap-2 mt-4">
        <Button disabled={isDisabled} type="button" onClick={onCancel}>
          {t.cancel}
        </Button>
        <Button disabled={isDisabled || !isValid} loading={loading} type="submit">
          {t.send.submit}
        </Button>
      </div>
    </form>
  );
}
